import { type ReactElement } from 'react';
import cx from 'clsx';
import { Menu, type MenuProps, Popover, type PopoverProps } from 'react-aria-components';
import styles from './menu-content.module.css';

interface MenuContentProps<T> extends MenuProps<T> {
  afterContent?: React.ReactNode;
  beforeContent?: React.ReactNode;
  placement?: PopoverProps['placement'];
  afterContentClassName?: string;
  beforeContentClassName?: string;
  popoverClassName?: string;
  portalContainerId?: string;
  crossOffset?: PopoverProps['crossOffset'];
}

export function MenuContent<T extends object>({
  className,
  afterContent,
  beforeContent,
  afterContentClassName,
  beforeContentClassName,
  popoverClassName,
  portalContainerId,
  placement = 'bottom start',
  crossOffset = 0,
  ...props
}: MenuContentProps<T>): ReactElement {
  // react-aria modal escapes the test container, we render it where it can find the modal (i.e. <div> component with 'ember-testing' id)
  const TEST_PORTAL_CONTAINER =
    ((portalContainerId ? document.getElementById(portalContainerId) : undefined) ||
      document.getElementById('ember-testing')) ??
    undefined;

  return (
    <Popover
      UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
      className={cx(styles.popover, popoverClassName)}
      crossOffset={crossOffset}
      offset={8}
      placement={placement}
    >
      {beforeContent ? (
        <div className={cx(styles.beforeContent, beforeContentClassName)}>{beforeContent}</div>
      ) : null}
      <Menu className={cx(styles.menu, className)} {...props} />
      {afterContent ? (
        <div className={cx(styles.afterContent, afterContentClassName)}>{afterContent}</div>
      ) : null}
    </Popover>
  );
}
